import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Table,
  Spinner,
  Modal,
  Form,
  Tabs,
  Tab,
  Badge,
  Row,
  Col
} from 'react-bootstrap';
import PageHeader from 'components/common/PageHeader';
import ApiService from 'helpers/apiService';

// Phase 1 раздела «Мониторинг» (см. docs/analysis/improvements/
// monitoring-and-product-management-design.md). На существующих эндпоинтах:
//   - Инфра: GET /health/upstreams (per-upstream gRPC state)
//   - Сводка: /dashboard/v1/stats + /aml/v1/alerts/count + /kyc/v1/count
// Единый формат индикатора {label,value,status,target,hint} со «светофором».
// Phase 2+: alert-state + Telegram + product-метрики backend (/monitoring/v1/*).

const statusBg = (s) => (s === 'good' ? 'success' : s === 'warn' ? 'warning' : s === 'bad' ? 'danger' : 'secondary');

const Indicator = ({ label, value, status, target, hint }) => (
  <Card className="mb-3 h-100">
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div className="text-muted small">{label}</div>
        <Badge bg={statusBg(status)}>&nbsp;</Badge>
      </div>
      <div className="fs-3 fw-bold">{value}</div>
      {target && <div className="text-muted small">Цель: {target}</div>}
      {hint && <div className="text-muted small fst-italic">{hint}</div>}
    </Card.Body>
  </Card>
);

const getAlertId = (row) => {
  if (row == null || typeof row !== 'object') return null;
  return row.id ?? row._id ?? row.alert_id ?? row.alertId ?? null;
};

// Карточка доступности сервиса (HTTP-код / latency / TLS до истечения).
const httpCodeColor = (code) => {
  if (!code) return 'text-danger';
  if (code >= 200 && code < 300) return 'text-success';
  if (code >= 300 && code < 400) return 'text-info';
  if (code >= 400 && code < 500) return 'text-warning';
  return 'text-danger';
};
const tlsColor = (days) => {
  if (days == null || days < 0) return 'text-muted';
  if (days < 14) return 'text-danger';
  if (days < 30) return 'text-warning';
  return 'text-success';
};
// OSS-MESH-1: форматирование инфра-метрик (cAdvisor).
const fmtBytes = (b) => {
  if (b == null || b <= 0) return '—';
  if (b >= 1 << 30) return `${(b / (1 << 30)).toFixed(1)} GB`;
  return `${(b / (1 << 20)).toFixed(0)} MB`;
};
const fmtUptime = (sec) => {
  if (sec == null || sec <= 0) return '—';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м`;
};
const cpuColor = (p) => (p == null ? '' : p >= 90 ? 'text-danger' : p >= 70 ? 'text-warning' : 'text-success');

const ServiceCard = ({ s }) => (
  <Card className="mb-3 h-100">
    <Card.Body className="py-2">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="fw-semibold text-truncate" style={{ maxWidth: 180 }} title={s.url}>{s.name}</div>
        <Badge bg={s.healthy ? 'success' : 'danger'}>{s.healthy ? 'OK' : 'НЕТ'}</Badge>
      </div>
      <div className="d-flex justify-content-between small py-1 border-top">
        <span className="text-muted">HTTP</span>
        <span className={`fw-semibold ${httpCodeColor(s.http_status)}`}>{s.http_status || (s.error ? '—' : '—')}</span>
      </div>
      <div className="d-flex justify-content-between small py-1 border-top">
        <span className="text-muted">Latency</span>
        <span>{s.latency_ms != null ? `${s.latency_ms} мс` : '—'}</span>
      </div>
      <div className="d-flex justify-content-between small py-1 border-top">
        <span className="text-muted">TLS до истечения</span>
        <span className={tlsColor(s.tls_expiry_days)}>{s.tls_expiry_days != null && s.tls_expiry_days >= 0 ? `${s.tls_expiry_days} дн.` : '—'}</span>
      </div>
      {s.infra && (
        <>
          <div className="d-flex justify-content-between small py-1 border-top">
            <span className="text-muted">CPU</span>
            <span className={cpuColor(s.infra.cpu_percent)}>{s.infra.cpu_percent != null ? `${s.infra.cpu_percent.toFixed(0)}%` : '—'}</span>
          </div>
          <div className="d-flex justify-content-between small py-1 border-top">
            <span className="text-muted">RAM</span>
            <span>{fmtBytes(s.infra.mem_bytes)}{s.infra.mem_limit_bytes > 0 ? ` / ${fmtBytes(s.infra.mem_limit_bytes)}` : ''}</span>
          </div>
          <div className="d-flex justify-content-between small py-1 border-top">
            <span className="text-muted">Uptime</span>
            <span>{fmtUptime(s.infra.uptime_sec)}</span>
          </div>
        </>
      )}
      {s.error && <div className="text-danger small mt-1 text-truncate" title={s.error}>{s.error}</div>}
    </Card.Body>
  </Card>
);

// Карточка БД (Postgres-метрики).
const DbCard = ({ d }) => (
  <Card className="mb-3 h-100">
    <Card.Body className="py-2">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div className="fw-semibold">{d.name} · {d.kind || 'postgres'}</div>
        <Badge bg={d.up ? 'success' : 'danger'}>{d.up ? 'UP' : 'НЕТ'}</Badge>
      </div>
      {d.up ? (
        <>
          <div className="d-flex justify-content-between small py-1 border-top"><span className="text-muted">Размер БД</span><span>{d.size_mb != null ? `${d.size_mb.toFixed(1)} MB` : '—'}</span></div>
          <div className="d-flex justify-content-between small py-1 border-top"><span className="text-muted">Активных коннектов</span><span>{d.connections ?? '—'}</span></div>
          <div className="d-flex justify-content-between small py-1 border-top"><span className="text-muted">Commits/sec</span><span>{d.commits_per_sec != null ? d.commits_per_sec.toFixed(2) : '—'}</span></div>
          <div className="d-flex justify-content-between small py-1 border-top"><span className="text-muted">Cache hit ratio</span><span className={d.cache_hit_ratio >= 95 ? 'text-success' : d.cache_hit_ratio >= 80 ? 'text-warning' : 'text-danger'}>{d.cache_hit_ratio != null ? `${d.cache_hit_ratio.toFixed(1)}%` : '—'}</span></div>
          <div className="d-flex justify-content-between small py-1 border-top"><span className="text-muted">Deadlocks</span><span className={d.deadlocks > 0 ? 'text-danger' : ''}>{d.deadlocks ?? 0}</span></div>
        </>
      ) : (
        <div className="text-danger small text-truncate" title={d.error}>{d.error || 'Недоступна'}</div>
      )}
    </Card.Body>
  </Card>
);

// ─────────────────────────── Вкладка «Инфра» ───────────────────────────
const InfraTab = () => {
  const [upstreams, setUpstreams] = useState([]);
  const [allHealthy, setAllHealthy] = useState(true);
  const [services, setServices] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const [upRes, svcRes, dbRes] = await Promise.allSettled([
      ApiService.healthUpstreams(),
      ApiService.monitoringServices(),
      ApiService.monitoringDatabases(),
    ]);
    if (upRes.status === 'fulfilled') {
      setUpstreams(upRes.value.upstreams);
      setAllHealthy(upRes.value.allHealthy);
    } else {
      setError(upRes.reason?.response?.data?.message || upRes.reason?.message || 'Ошибка загрузки состояния сервисов');
      setUpstreams([]);
    }
    if (svcRes.status === 'fulfilled') setServices(svcRes.value.services);
    else setServices([]);
    if (dbRes.status === 'fulfilled') setDatabases(dbRes.value.databases);
    else setDatabases([]);
    setLoading(false);
  }, []);

  // US-MON-01: автообновление 30с (пропускаем, когда вкладка браузера неактивна).
  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      load();
    }, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const healthyCount = upstreams.filter((u) => u.healthy).length;
  const svcHealthy = services.filter((s) => s.healthy).length;

  return (
    <>
      {/* Карточки доступности сервисов (HTTP/latency/TLS) */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Card.Title className="mb-0">Доступность сервисов</Card.Title>
            {!loading && services.length > 0 && (
              <Badge bg={svcHealthy === services.length ? 'success' : 'danger'}>{svcHealthy}/{services.length} OK</Badge>
            )}
          </div>
          <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Обновить</Button>
        </Card.Header>
        <Card.Body>
          {loading && !services.length ? (
            <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>
          ) : !services.length ? (
            <div className="text-muted">Нет данных (эндпоинт /monitoring/v1/services недоступен или admin-api не задеплоен)</div>
          ) : (
            <Row>
              {services.map((s) => (
                <Col key={s.name} xs={12} sm={6} lg={4} xxl={3}><ServiceCard s={s} /></Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Базы данных (Postgres-метрики) */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Card.Title className="mb-0">Базы данных</Card.Title>
            {!loading && databases.length > 0 && (
              <Badge bg={databases.every((d) => d.up) ? 'success' : 'danger'}>
                {databases.filter((d) => d.up).length}/{databases.length} UP
              </Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {loading && !databases.length ? (
            <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>
          ) : !databases.length ? (
            <div className="text-muted">Нет данных (эндпоинт /monitoring/v1/databases недоступен или admin-api не задеплоен)</div>
          ) : (
            <Row>
              {databases.map((d) => (
                <Col key={d.name} xs={12} sm={6} lg={4} xxl={3}><DbCard d={d} /></Col>
              ))}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* gRPC-апстримы admin-api (состояние каналов) */}
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <Card.Title className="mb-0">gRPC-апстримы admin-api</Card.Title>
            {!loading && !error && (
              <Badge bg={allHealthy ? 'success' : 'danger'}>{healthyCount}/{upstreams.length} healthy</Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading && !upstreams.length ? (
            <div className="d-flex align-items-center gap-2 p-4"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>
          ) : error ? (
            <div className="p-4 text-danger">{error}</div>
          ) : !upstreams.length ? (
            <div className="p-4 text-muted">Нет данных по апстримам</div>
          ) : (
            <Table responsive hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr><th>Сервис</th><th>Адрес</th><th>Состояние</th><th>Здоров</th><th>Проверка, мс</th></tr>
              </thead>
              <tbody>
                {upstreams.map((u) => (
                  <tr key={u.name}>
                    <td>{u.name}</td>
                    <td className="text-muted small">{u.addr || '—'}</td>
                    <td>{u.state}</td>
                    <td><Badge bg={u.healthy ? 'success' : 'danger'}>{u.healthy ? 'OK' : 'НЕТ'}</Badge></td>
                    <td className="text-muted small">{u.check_ms ?? u.checkedMs ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        <Card.Footer className="text-muted small">
          «Базы данных» и Synthetic E2E — следующие фазы. Алертинг в Telegram — отдельной фазой.
        </Card.Footer>
      </Card>
    </>
  );
};

// ─────────────────────────── Вкладка «Сводка» ───────────────────────────
const SummaryTab = () => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, amlRes, kycRes] = await Promise.allSettled([
        ApiService.dashboardStats(),
        ApiService.amlGetActiveCount(),
        ApiService.kycGetActiveCount(),
      ]);
      const stats = statsRes.status === 'fulfilled' ? statsRes.value : {};
      const aml = amlRes.status === 'fulfilled' ? amlRes.value : 0;
      const kyc = kycRes.status === 'fulfilled' ? kycRes.value : 0;
      setIndicators([
        { id: 'operators', label: 'Операторы', value: stats.operators ?? '—', status: 'good', hint: 'Активные операторы админки' },
        { id: 'sessions', label: 'Активные сессии', value: stats.active_sessions ?? '—', status: 'good' },
        { id: 'actions_today', label: 'Действий сегодня', value: stats.actions_today ?? '—', status: 'good', hint: 'Записей в audit-логе за сутки' },
        { id: 'aml_active', label: 'AML: активные кейсы', value: aml, status: aml === 0 ? 'good' : aml > 50 ? 'bad' : 'warn', target: 'меньше — лучше', hint: 'pending + in_review' },
        { id: 'kyc_active', label: 'KYC: в очереди', value: kyc, status: kyc === 0 ? 'good' : kyc > 50 ? 'bad' : 'warn', target: 'меньше — лучше', hint: 'PENDING + ON_HOLD' },
      ]);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка загрузки сводки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">Сводка</Card.Title>
        <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Обновить</Button>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <div className="d-flex align-items-center gap-2 p-2"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : (
          <Row>
            {indicators.map((ind) => (
              <Col key={ind.id} xs={12} sm={6} lg={4} xxl={3}><Indicator {...ind} /></Col>
            ))}
          </Row>
        )}
        <div className="text-muted small mt-2">
          Финтех-воронка (Регистрация→KYC→Счёт→Депозит), экономика и тренды — Phase 1.5/2 (backend
          /monitoring/v1/product/*).
        </div>
      </Card.Body>
    </Card>
  );
};

// ─────────────────────── Вкладка «Оповещения» (CRUD) ───────────────────────
const AlertsTab = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [addPayload, setAddPayload] = useState('{}');
  const [editPayload, setEditPayload] = useState('{}');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ApiService.supportListAlerts();
      setAlerts(Array.isArray(data) ? data : data ? [data] : []);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 501
        ? 'Функционал в разработке'
        : (err?.response?.data?.message || err?.message || 'Ошибка загрузки оповещений'));
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const handleAdd = async () => {
    let payload;
    try { payload = JSON.parse(addPayload); } catch { setSubmitError('Неверный JSON'); return; }
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      await ApiService.supportAddAlerts(payload);
      setShowAddModal(false);
      setAddPayload('{}');
      loadAlerts();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Ошибка добавления');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingAlert) return;
    let payload;
    try { payload = JSON.parse(editPayload); } catch { setSubmitError('Неверный JSON'); return; }
    setSubmitLoading(true);
    setSubmitError(null);
    try {
      await ApiService.supportEditAlerts(payload);
      setShowEditModal(false);
      setEditingAlert(null);
      setEditPayload('{}');
      loadAlerts();
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Ошибка сохранения');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const id = getAlertId(row);
    if (id == null) {
      if (window.confirm('Не найден идентификатор. Удалить, передав объект в params?')) {
        try { await ApiService.supportDeleteAlerts(row); loadAlerts(); }
        catch (err) { alert(err?.response?.data?.message || err?.message || 'Ошибка удаления'); }
      }
      return;
    }
    if (!window.confirm('Удалить оповещение?')) return;
    try { await ApiService.supportDeleteAlerts({ id: String(id) }); loadAlerts(); }
    catch (err) { alert(err?.response?.data?.message || err?.message || 'Ошибка удаления'); }
  };

  const openEdit = (row) => {
    setEditingAlert(row);
    setEditPayload(JSON.stringify(row, null, 2));
    setSubmitError(null);
    setShowEditModal(true);
  };

  const keys = alerts.length && typeof alerts[0] === 'object' && alerts[0] !== null ? Object.keys(alerts[0]) : [];

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Конфигурации оповещений</Card.Title>
          <Button variant="primary" size="sm" onClick={() => { setAddPayload('{}'); setSubmitError(null); setShowAddModal(true); }}>Добавить</Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="d-flex align-items-center gap-2 p-4"><Spinner animation="border" size="sm" /><span>Загрузка...</span></div>
          ) : error ? (
            <div className={`p-4 ${error === 'Функционал в разработке' ? 'text-muted' : 'text-danger'}`}>{error}</div>
          ) : !alerts.length ? (
            <div className="p-4 text-muted">Нет оповещений</div>
          ) : keys.length > 0 ? (
            <Table responsive hover size="sm" className="align-middle mb-0" style={{ fontSize: '0.82rem' }}>
              <thead className="table-light">
                <tr>{keys.map((k) => <th key={k}>{k}</th>)}<th style={{ width: 140 }}>Действия</th></tr>
              </thead>
              <tbody>
                {alerts.map((row, idx) => (
                  <tr key={getAlertId(row) ?? idx}>
                    {keys.map((k) => (
                      <td key={k}>{row[k] != null && typeof row[k] === 'object' ? JSON.stringify(row[k]) : String(row[k] ?? '')}</td>
                    ))}
                    <td>
                      <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEdit(row)}>Изменить</Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row)}>Удалить</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <ul className="list-group list-group-flush">
              {alerts.map((item, idx) => (
                <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                  <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item)}>Удалить</Button>
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton><Modal.Title>Добавить оповещение</Modal.Title></Modal.Header>
        <Modal.Body>
          {submitError && <div className="text-danger small mb-2">{submitError}</div>}
          <Form.Group>
            <Form.Label>Данные (JSON)</Form.Label>
            <Form.Control as="textarea" rows={6} value={addPayload} onChange={(e) => setAddPayload(e.target.value)} className="font-monospace" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>Отмена</Button>
          <Button variant="primary" onClick={handleAdd} disabled={submitLoading}>
            {submitLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}Добавить
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => { setShowEditModal(false); setEditingAlert(null); }}>
        <Modal.Header closeButton><Modal.Title>Редактировать оповещение</Modal.Title></Modal.Header>
        <Modal.Body>
          {submitError && <div className="text-danger small mb-2">{submitError}</div>}
          <Form.Group>
            <Form.Label>Данные (JSON)</Form.Label>
            <Form.Control as="textarea" rows={6} value={editPayload} onChange={(e) => setEditPayload(e.target.value)} className="font-monospace" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowEditModal(false); setEditingAlert(null); }}>Отмена</Button>
          <Button variant="primary" onClick={handleEdit} disabled={submitLoading}>
            {submitLoading ? <Spinner animation="border" size="sm" className="me-1" /> : null}Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ─────────────────────────── Вкладка «E2E» (Synthetic) ───────────────────────────
const relativeTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'только что';
  if (mins < 60) return `${mins} мин назад`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} ч назад`;
  return `${Math.floor(h / 24)} дн назад`;
};
const E2ECard = ({ s }) => {
  const pct = s.total_24h > 0 ? Math.round((s.ok_24h / s.total_24h) * 100) : null;
  return (
    <Card className="mb-3 h-100 border" style={{ borderColor: s.last_ok ? undefined : '#dc3545' }}>
      <Card.Body className="py-2">
        <div className="d-flex justify-content-between align-items-start">
          <div className="fw-semibold">{s.name || s.code}</div>
          <Badge bg={s.last_ok ? 'success' : 'danger'}>{s.last_ok ? '✓' : '✕'}</Badge>
        </div>
        <div className="d-flex justify-content-between small mt-2">
          <span className="text-muted">{relativeTime(s.last_at)}</span>
          <span>{s.last_latency_ms != null ? `${s.last_latency_ms} мс` : '—'}</span>
        </div>
        <div className="text-muted small mt-1">
          24ч: {s.ok_24h ?? 0}/{s.total_24h ?? 0}{pct != null ? ` (${pct}%)` : ''}
        </div>
      </Card.Body>
    </Card>
  );
};
const E2ETab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { setItems(await ApiService.monitoringE2E()); } catch { setItems([]); } finally { setLoading(false); }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      load();
    }, 30_000);
    return () => clearInterval(id);
  }, [load]);
  const okCount = items.filter((s) => s.last_ok).length;
  // E2E-SVC-1: группируем сценарии по сервису для удобного просмотра.
  const groups = React.useMemo(() => {
    const map = new Map();
    items.forEach((s) => {
      const svc = s.service || 'Прочее';
      if (!map.has(svc)) map.set(svc, []);
      map.get(svc).push(s);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);
  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <Card.Title className="mb-0">Synthetic E2E</Card.Title>
          {!loading && items.length > 0 && (
            <Badge bg={okCount === items.length ? 'success' : 'danger'}>{okCount}/{items.length} OK</Badge>
          )}
        </div>
        <Button variant="outline-secondary" size="sm" onClick={load} disabled={loading}>Обновить</Button>
      </Card.Header>
      <Card.Body>
        {loading && !items.length ? (
          <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span>Загрузка…</span></div>
        ) : !items.length ? (
          <div className="text-muted">Нет данных (раннер ещё не отработал или admin-api не задеплоен). Сценарии запускаются на интервале (по умолчанию 5 мин).</div>
        ) : (
          groups.map(([svc, list]) => {
            const okN = list.filter((s) => s.last_ok).length;
            return (
              <div key={svc} className="mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <h6 className="mb-0 text-uppercase text-muted" style={{ fontSize: '0.75rem', letterSpacing: '0.04em' }}>{svc}</h6>
                  <Badge bg={okN === list.length ? 'success' : 'danger'} style={{ fontSize: '0.7rem' }}>{okN}/{list.length}</Badge>
                </div>
                <Row>
                  {list.map((s) => (
                    <Col key={s.code} xs={12} sm={6} lg={4} xxl={3}><E2ECard s={s} /></Col>
                  ))}
                </Row>
              </div>
            );
          })
        )}
      </Card.Body>
      <Card.Footer className="text-muted small">
        Триггер при провале (Telegram-алерт) — следующая фаза. Мутирующие сценарии (регистрация/логин/отправка) — после выделенного тест-аккаунта.
      </Card.Footer>
    </Card>
  );
};

const SupportMonitoring = () => (
  <>
    <PageHeader title="Мониторинг" description="Инфра-здоровье, продуктовая сводка, E2E и оповещения" className="mb-3" />
    <Tabs defaultActiveKey="summary" id="monitoring-tabs" className="mb-3">
      <Tab eventKey="summary" title="Сводка"><SummaryTab /></Tab>
      <Tab eventKey="infra" title="Инфра"><InfraTab /></Tab>
      <Tab eventKey="e2e" title="E2E"><E2ETab /></Tab>
      <Tab eventKey="alerts" title="Оповещения"><AlertsTab /></Tab>
    </Tabs>
  </>
);

export default SupportMonitoring;
